import type { Dispatch, KeyboardEvent, ReactNode } from "react"

import type { SegmentAnalysisAction, SegmentAnalysisState } from "../hooks/use-segment-analysis"
import type { SegmentCreateDraft } from "../types"

import { Check, ChevronLeft, Plus, X, Zap } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { marketColors } from "../data/mock-segment-analysis-data"
import { APP_NAME } from "../utils/branding"

type SegmentWizardProps = {
  dispatch: Dispatch<SegmentAnalysisAction>
  onCreate: (draft: SegmentCreateDraft) => void
  state: SegmentAnalysisState
}

const wizardSteps = [
  { label: "Basics", title: "Name your segment" },
  { label: "Description", title: "Describe the focus" },
  { label: "Geography", title: "Where does this segment matter?" },
  { label: "Players", title: "Known customers & competitors" },
  { label: "Sources", title: "Trusted sources" },
]

const regions = [
  "EU",
  "US",
  "UK",
  "Nordics",
  "DACH",
  "China",
  "SE Asia",
  "LATAM",
  "Middle East",
  "Global",
]

export function SegmentWizard({ dispatch, onCreate, state }: SegmentWizardProps) {
  const step = wizardSteps[state.wizardStep] ?? wizardSteps[0]
  const isLastStep = state.wizardStep === wizardSteps.length - 1

  // Step 1 (Basics) requires a segment name and a market group before advancing
  // (ledger #17). Other steps are optional, so Continue is only gated on step 0.
  const basicsValid =
    state.wizardData.name.trim().length > 0 && state.wizardData.market.trim().length > 0
  const canAdvance = state.wizardStep !== 0 || basicsValid

  // Escape closes the wizard (ledger #18) via Dialog's own layered handling:
  // Radix dismisses only the topmost open layer, so Escape first closes an
  // open Select and only then the wizard — a manual window listener here
  // would tear down the whole wizard through an open dropdown.
  const handleOpenChange = (open: boolean) => {
    if (!open) dispatch({ type: "closeWizard" })
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        className="grid max-h-[calc(100vh-2rem)] max-w-3xl grid-rows-[auto_1fr_auto] gap-0 overflow-hidden p-0"
        data-sa-surface=""
        // A stray backdrop click must not drop in-progress wizard fields;
        // Escape and the explicit X / Cancel remain the only exits.
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="border-border space-y-0 border-b p-5 pr-12 text-left">
          <p className="text-primary text-xs font-semibold tracking-[0.16em] uppercase">
            Step {state.wizardStep + 1} of {wizardSteps.length}
          </p>
          <DialogTitle className="mt-1 text-xl">{step.title}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-5 overflow-auto p-5">
          <div className="flex gap-2 overflow-x-auto">
            {wizardSteps.map((item, index) => {
              const isActive = index === state.wizardStep
              const isComplete = index < state.wizardStep

              return (
                <div
                  key={item.label}
                  className={cn(
                    "flex min-w-24 flex-1 items-center gap-2 rounded-md px-3 py-2 text-xs font-medium transition-colors",
                    isActive && "bg-primary/10 text-primary ring-primary/20 ring-1",
                    isComplete && "bg-success/10 text-success ring-success/20 ring-1",
                    !isActive && !isComplete && "bg-muted text-muted-foreground"
                  )}
                >
                  {isComplete ? (
                    <Check className="check-animated size-3.5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                  {item.label}
                </div>
              )
            })}
          </div>
          <div
            key={state.wizardStep}
            className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
          >
            {renderStep(state, dispatch)}
          </div>
        </div>
        <DialogFooter className="border-border flex-row items-center justify-between border-t p-5 sm:justify-between sm:space-x-0">
          <Button
            className="gap-2"
            variant="ghost"
            onClick={() =>
              state.wizardStep === 0
                ? dispatch({ type: "closeWizard" })
                : dispatch({ type: "wizardBack" })
            }
          >
            <ChevronLeft className="size-4" />
            {state.wizardStep === 0 ? "Cancel" : "Back"}
          </Button>
          {isLastStep ? (
            <Button className="gap-2 shadow-sm" onClick={() => onCreate(state.wizardData)}>
              <Zap className="size-4" />
              Save & run baseline
            </Button>
          ) : (
            <div className="flex items-center gap-3">
              {state.wizardStep === 0 && !basicsValid ? (
                <span className="text-muted-foreground text-xs" data-testid="wizard-basics-hint">
                  Add a segment name and market group to continue.
                </span>
              ) : null}
              <Button
                aria-disabled={!canAdvance}
                className="shadow-sm"
                data-testid="wizard-continue"
                disabled={!canAdvance}
                onClick={() => {
                  if (canAdvance) dispatch({ type: "wizardNext" })
                }}
              >
                Continue
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function renderStep(state: SegmentAnalysisState, dispatch: Dispatch<SegmentAnalysisAction>) {
  switch (state.wizardStep) {
    case 0:
      return (
        <div className="grid gap-4">
          <Field label="Segment name">
            <Input
              className="min-h-10"
              placeholder="e.g. Industrial 3D printing"
              value={state.wizardData.name}
              onChange={(event) =>
                dispatch({ type: "setWizardField", field: "name", value: event.target.value })
              }
            />
          </Field>
          <Field label="Market group">
            <Select
              value={state.wizardData.market || undefined}
              onValueChange={(value) =>
                dispatch({ type: "setWizardField", field: "market", value })
              }
            >
              <SelectTrigger className="min-h-10">
                <SelectValue placeholder="Select market group..." />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(marketColors).map((market) => (
                  <SelectItem key={market} value={market}>
                    {market}
                  </SelectItem>
                ))}
                <SelectItem value="__new__">+ Suggest a new market group...</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      )
    case 1:
      return (
        <div className="grid gap-4">
          <Field label="Short description">
            <Textarea
              className="min-h-28 resize-y py-3"
              placeholder="1-2 sentences on what this segment is and why we care."
              value={state.wizardData.description}
              onChange={(event) =>
                dispatch({
                  type: "setWizardField",
                  field: "description",
                  value: event.target.value,
                })
              }
            />
          </Field>
          <TagField
            dispatch={dispatch}
            field="keywords"
            label="Primary keywords"
            placeholder="add keyword + Enter"
            state={state}
          />
        </div>
      )
    case 2:
      return (
        <Field label="Geography focus">
          <div className="flex flex-wrap gap-2">
            {regions.map((region) => (
              <button
                key={region}
                type="button"
                className={cn(
                  "btn-spring focus-visible:ring-ring rounded-md px-3 py-1.5 text-sm font-medium transition focus-visible:ring-2 focus-visible:outline-none",
                  state.wizardData.geo.includes(region)
                    ? "border-foreground bg-primary text-primary-foreground border shadow-sm"
                    : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent"
                )}
                onClick={() => dispatch({ type: "toggleWizardGeo", geo: region })}
              >
                {region}
              </button>
            ))}
          </div>
        </Field>
      )
    case 3:
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <TagField
            dispatch={dispatch}
            field="customers"
            label="Known customers"
            placeholder="Add customer + Enter"
            state={state}
          />
          <TagField
            dispatch={dispatch}
            field="competitors"
            label="Known competitors"
            placeholder="Add competitor + Enter"
            state={state}
          />
        </div>
      )
    case 4:
      return (
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <TagField
              dispatch={dispatch}
              field="sources"
              label="Trusted sources"
              placeholder="Paste a URL + Enter"
              state={state}
            />
            <SourcesHint count={state.wizardData.sources.length} />
          </div>
          <div className="border-primary/20 bg-primary/10 text-foreground rounded-lg border p-4 text-sm">
            <p className="text-primary mb-2 inline-flex items-center gap-2 font-semibold">
              <Zap className="size-4" />
              {APP_NAME} will auto-draft on save
            </p>
            <p>Name: {state.wizardData.name || "(unnamed)"}</p>
            <p>Market group: {state.wizardData.market || "(not set)"}</p>
            <p>
              Geography: {state.wizardData.geo.length ? state.wizardData.geo.join(", ") : "Global"}
            </p>
          </div>
        </div>
      )
    default:
      return null
  }
}

/**
 * Soft guidance toward 3–5 trusted sources (§5.5). Non-blocking: it only nudges —
 * the user can still continue with any count.
 */
function SourcesHint({ count }: { count: number }) {
  let message = "Aim for 3–5 trusted sources for the best baseline."
  let tone = "text-muted-foreground"
  if (count > 0 && count < 3) {
    message = `${count} added — a few more (3–5) gives the agents better coverage.`
    tone = "text-warning-foreground"
  } else if (count > 5) {
    message = `${count} added — 3–5 focused sources usually work better than a long list.`
    tone = "text-warning-foreground"
  } else if (count >= 3) {
    message = `${count} sources — good coverage.`
    tone = "text-success"
  }
  return <p className={cn("text-xs", tone)}>{message}</p>
}

function Field({ children, label }: { children: ReactNode; label: string }) {
  return (
    <label className="text-foreground grid gap-2 text-sm font-medium">
      {label}
      {children}
    </label>
  )
}

function TagField({
  dispatch,
  field,
  label,
  placeholder,
  state,
}: {
  dispatch: Dispatch<SegmentAnalysisAction>
  field: "keywords" | "customers" | "competitors" | "sources"
  label: string
  placeholder: string
  state: SegmentAnalysisState
}) {
  const values = state.wizardData[field]

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return
    event.preventDefault()
    dispatch({ type: "addWizardTag", field, value: event.currentTarget.value })
    event.currentTarget.value = ""
  }

  return (
    <Field label={label}>
      <div className="border-input bg-background focus-within:border-primary focus-within:ring-primary/15 flex min-h-10 flex-wrap items-center gap-2 rounded-md border p-2 shadow-sm transition focus-within:ring-[3px]">
        {values.map((value, index) => (
          <span
            key={value}
            className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
          >
            {value}
            <button
              className="btn-spring hover:bg-primary/15 -m-1 flex size-6 items-center justify-center rounded-full"
              type="button"
              onClick={() => dispatch({ type: "removeWizardTag", field, index })}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          className="text-foreground placeholder:text-muted-foreground min-w-40 flex-1 bg-transparent text-sm outline-none"
          placeholder={values.length ? "" : placeholder}
          onKeyDown={handleKeyDown}
        />
        <Plus className="text-muted-foreground size-4" />
      </div>
    </Field>
  )
}
